import React, { useState, useEffect, useRef, useCallback } from 'react';

const GOOGLE_MAPS_KEY = 'AIzaSyD5uTua7Yy1y_2zKio3vfwcyz2tuStE2e8';
const REFRESH_INTERVAL = 30000;

const TechnicianMap = ({ isVisible, onClose }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const trailPolylinesRef = useRef({});
  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [trailData, setTrailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return;
    if (window.google?.maps) { setMapReady(true); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=geometry`;
    script.async = true;
    script.onload = () => setMapReady(true);
    document.head.appendChild(script);
  }, [isVisible]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 30.0444, lng: 31.2357 },
      zoom: 11,
      styles: darkMapStyle,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });
  }, [mapReady]);

  const updateMarkers = useCallback((techs) => {
    if (!mapInstanceRef.current || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    const existingIds = new Set(Object.keys(markersRef.current));

    techs.forEach(tech => {
      const id = String(tech.technician_id);
      existingIds.delete(id);
      const pos = { lat: parseFloat(tech.latitude), lng: parseFloat(tech.longitude) };
      bounds.extend(pos);

      if (markersRef.current[id]) {
        markersRef.current[id].setPosition(pos);
        markersRef.current[id].techData = tech;
      } else {
        const color = tech.dispatch_color || '#3b82f6';
        const marker = new window.google.maps.Marker({
          position: pos,
          map: mapInstanceRef.current,
          title: tech.technician_name,
          icon: createTechIcon(color, tech.is_moving),
        });
        marker.techData = tech;
        const infoWindow = new window.google.maps.InfoWindow();
        marker.addListener('click', () => {
          infoWindow.setContent(createInfoContent(tech));
          infoWindow.open(mapInstanceRef.current, marker);
        });
        markersRef.current[id] = marker;
      }
    });

    existingIds.forEach(id => {
      markersRef.current[id]?.setMap(null);
      delete markersRef.current[id];
    });

    if (techs.length > 0) mapInstanceRef.current.fitBounds(bounds, { padding: 60 });
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/fsm/gps/technicians', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTechnicians(data.data || []);
        updateMarkers(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch technician locations:', err);
    } finally {
      setLoading(false);
    }
  }, [updateMarkers]);

  useEffect(() => {
    if (!isVisible || !mapReady) return;
    fetchLocations();
    intervalRef.current = setInterval(fetchLocations, REFRESH_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [isVisible, mapReady, fetchLocations]);

  const showTrail = async (techId) => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`/api/v1/fsm/gps/technician/${techId}?date=${today}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data.length > 0) {
        setTrailData({ techId, points: data.data });
        drawTrail(techId, data.data);
      }
    } catch (err) { console.error('Failed to fetch trail:', err); }
  };

  const drawTrail = (techId, points) => {
    if (!mapInstanceRef.current || !window.google) return;
    Object.values(trailPolylinesRef.current).forEach(p => p.setMap(null));
    trailPolylinesRef.current = {};
    const tech = technicians.find(t => t.technician_id === techId);
    const color = tech?.dispatch_color || '#3b82f6';
    const path = points.map(p => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));
    const polyline = new window.google.maps.Polyline({
      path, geodesic: true, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 3,
    });
    polyline.setMap(mapInstanceRef.current);
    trailPolylinesRef.current[techId] = polyline;
  };

  const clearTrail = () => {
    Object.values(trailPolylinesRef.current).forEach(p => p.setMap(null));
    trailPolylinesRef.current = {};
    setTrailData(null);
    setSelectedTech(null);
  };

  // Cleanup on close
  useEffect(() => {
    if (!isVisible) {
      Object.values(markersRef.current).forEach(m => m.setMap(null));
      markersRef.current = {};
      Object.values(trailPolylinesRef.current).forEach(p => p.setMap(null));
      trailPolylinesRef.current = {};
      mapInstanceRef.current = null;
      setTechnicians([]);
      setSelectedTech(null);
      setTrailData(null);
      setLoading(true);
      setMapReady(!!window.google?.maps);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <div style={st.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: '#f9fafb', fontSize: 16, fontWeight: 700 }}>Live Technician Locations</span>
            <span style={{ color: '#3b82f6', fontSize: 13, fontWeight: 600, backgroundColor: 'rgba(59,130,246,0.15)', padding: '3px 10px', borderRadius: 10 }}>
              {technicians.length} active
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {trailData && <button style={st.clearTrailBtn} onClick={clearTrail}>Clear Trail</button>}
            <button style={st.refreshBtn} onClick={fetchLocations}>&#8635; Refresh</button>
            <button style={st.closeBtn} onClick={onClose}>&#10005;</button>
          </div>
        </div>
        <div style={st.body}>
          <div style={st.sidebar}>
            {loading ? (
              <div style={{ color: '#9ca3af', textAlign: 'center', paddingTop: 40 }}>Loading...</div>
            ) : technicians.length === 0 ? (
              <div style={{ color: '#6b7280', textAlign: 'center', paddingTop: 40, fontSize: 13 }}>No active locations</div>
            ) : (
              technicians.map(tech => (
                <div key={tech.technician_id}
                  style={{
                    ...st.techCard,
                    borderLeftColor: tech.dispatch_color || '#3b82f6',
                    backgroundColor: selectedTech?.technician_id === tech.technician_id ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.03)',
                  }}
                  onClick={() => {
                    setSelectedTech(tech);
                    if (mapInstanceRef.current) {
                      mapInstanceRef.current.panTo({ lat: parseFloat(tech.latitude), lng: parseFloat(tech.longitude) });
                      mapInstanceRef.current.setZoom(15);
                    }
                  }}>
                  <div style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 600 }}>{tech.technician_name}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ color: tech.is_moving ? '#10b981' : '#9ca3af', fontSize: 11 }}>
                      {tech.is_moving ? 'Moving' : 'Stationary'}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: 11 }}>
                      {tech.minutes_ago < 1 ? 'Just now' : `${tech.minutes_ago}m ago`}
                    </span>
                  </div>
                  {tech.battery_level != null && (
                    <div style={{ fontSize: 11, color: tech.battery_level < 20 ? '#ef4444' : '#9ca3af', marginTop: 3 }}>
                      Battery: {tech.battery_level}%
                    </div>
                  )}
                  <button
                    style={st.trailBtn}
                    onClick={(e) => { e.stopPropagation(); showTrail(tech.technician_id); }}>
                    Show Trail
                  </button>
                </div>
              ))
            )}
          </div>
          <div ref={mapRef} style={st.map} />
        </div>
      </div>
    </div>
  );
};

function createTechIcon(color, isMoving) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40"><path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24C32 7.16 24.84 0 16 0z" fill="${color}" stroke="#fff" stroke-width="2"/><circle cx="16" cy="15" r="7" fill="#fff"/>${isMoving ? '<circle cx="16" cy="15" r="4" fill="' + color + '"/>' : '<rect x="13" y="12" width="6" height="6" fill="' + color + '" rx="1"/>'}</svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(32, 40),
    anchor: new window.google.maps.Point(16, 40),
  };
}

function createInfoContent(tech) {
  return `<div style="font-family:-apple-system,sans-serif;padding:8px;min-width:180px;background:#1e293b;border-radius:8px;color:#e2e8f0">
    <div style="font-weight:700;font-size:14px;margin-bottom:6px;color:#f1f5f9">${tech.technician_name}</div>
    <div style="font-size:12px;color:#94a3b8;margin-bottom:3px">${tech.is_moving ? '<span style=color:#10b981>Moving</span>' : '<span style=color:#9ca3af>Stationary</span>'} &middot; ${tech.minutes_ago < 1 ? 'Just now' : tech.minutes_ago + 'm ago'}</div>
    ${tech.battery_level != null ? '<div style="font-size:12px;color:' + (tech.battery_level < 20 ? '#ef4444' : '#94a3b8') + '">Battery: ' + tech.battery_level + '%</div>' : ''}
    ${tech.speed != null ? '<div style="font-size:12px;color:#94a3b8">Speed: ' + Math.round(tech.speed * 3.6) + ' km/h</div>' : ''}
  </div>`;
}

const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#255763' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2c6675' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#283d6a' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
];

const st = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { width: '92vw', height: '85vh', maxWidth: 1400, backgroundColor: '#0f172a', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '1px solid rgba(255,255,255,0.1)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(30,41,59,0.6)' },
  closeBtn: { background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  refreshBtn: { background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#3b82f6', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  clearTrailBtn: { background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  body: { flex: 1, display: 'flex', overflow: 'hidden' },
  sidebar: { width: 260, overflowY: 'auto', padding: 12, borderRight: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(15,23,42,0.9)' },
  map: { flex: 1 },
  techCard: { padding: '10px 12px', borderRadius: 8, marginBottom: 8, cursor: 'pointer', borderLeft: '3px solid', transition: 'background 0.15s' },
  trailBtn: { marginTop: 6, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#9ca3af', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, width: '100%' },
};

export default TechnicianMap;
